<template>
  <div class="mobile-condition-select">
    <!-- 移动端查询条件：纯文字（无背景色），点开底部选择框，在框内选值或在框内清除 -->
    <div class="condition-area" @click="toShowDrawer">
      <span class="text" :class="{placeholder: !showValue}">{{ showValue || placeholder }}</span>
      <yq-icon class="arrow-down" name="element-ui_arrow-down"/>
    </div>
    <!--
      抽屉头部：清空 - 标题 - 确认，与 MobileSearchSelect / MobileEmployeeSelect 同一套。
      ★ 左边原本是「取消」，但点遮罩本身就是取消，这个按钮是多余的；
        原来放在内容区顶部的「清除项」挪到这里，操作位置不变、少占一行。
    -->
    <mobile-drawer v-model="showDrawer" :title="drawerTitle" cancel-text="清空"
                   @confirm="toConfirm" @cancel="toClear">
      <div class="condition-data">
        <!-- 普通列表 -->
        <div v-if="type === 'list'" class="data-list">
          <div v-for="item in dataList" :key="item[codeColumn]" class="data-item" @click="selectData(item)">
            <div class="column-label">{{ item[labelColumn] }}</div>
            <yq-icon v-if="modelValue === item[codeColumn]" class="selected" name="element-ui_check"/>
          </div>
        </div>
        <!-- 树：逐级进入，面包屑可回上一级 -->
        <div v-if="type === 'tree'" class="tree">
          <div class="header-selected">
            <!--
              根节点：点它回到最高一层。
              没有它的话，进了第二层就只能「选第二层」或「确认」，没法退回来重选第一层的节点。
            -->
            <span class="item" @click="toRootLevel">
              <span class="label">{{ rootLabel }}</span>
              <yq-icon class="arrow" name="element-ui_arrow-right" v-if="selectedList.length > 0"/>
            </span>
            <span v-for="(item, index) in selectedList" :key="item[codeColumn]" class="item" @click="toParentLevel(item)">
              <span class="label">{{ item[labelColumn] }}</span>
              <yq-icon class="arrow" name="element-ui_arrow-right" v-if="index < selectedList.length - 1"/>
            </span>
          </div>
          <div class="tree-list">
            <div v-for="item in currentList" :key="item[codeColumn]" class="data-item" @click="selectTreeData(item)">
              <div class="column-label">
                <yq-icon v-if="hasChildren(item)" name="element-ui_arrow-right"/>
                <span>{{ item[labelColumn] }}</span>
              </div>
              <yq-icon v-if="modelValue === item[codeColumn]" class="selected" name="element-ui_check"/>
            </div>
          </div>
        </div>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import {watch} from "vue";
import YqIcon from "@/components/YqIcon.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";

export default {
  emits: ["update:modelValue", "change"],
  props: {
    modelValue: {default: ""},
    type: { // list（普通列表）、tree（树）
      type: String,
      default: "list"
    },
    data: {default: () => []},
    labelColumn: {default: "label"},   // 显示文案取哪个字段
    codeColumn: {default: "value"},    // 值取哪个字段
    childrenColumn: {default: "children"},
    placeholder: {default: "请选择"},   // 没有数据时显示的提示词（一般就是条件名，如「客户分类」）
    rootLabel: {default: "全部"},       // 树面包屑最前面的根节点文案
    title: {default: ""},              // 选择框标题，默认「选择 + placeholder」
  },
  name: "MobileConditionSelect",
  components: {
    YqIcon, MobileDrawer
  },
  data() {
    return {
      showDrawer: false,
      showValue: "",     // 当前条件的显示文案，空则显示 placeholder
      dataList: [],
      currentList: [],   // 树当前展示的一层
      selectedList: [],  // 树上已进入的路径
    };
  },
  computed: {
    drawerTitle() {
      return this.title || (this.placeholder ? "选择" + this.placeholder : "选择");
    }
  },
  created() {
    this.dataList = this.data;
    this.updateShowValue();
    watch(() => this.data, () => {
      this.dataList = this.data;
      this.updateShowValue();
    })
    watch(() => this.modelValue, () => this.updateShowValue())
  },
  methods: {
    // 回显：按值在数据里找到对应文案
    updateShowValue() {
      this.showValue = "";
      if (this.modelValue !== undefined && this.modelValue !== "") {
        this.findLabel(this.modelValue, this.dataList);
      }
    },
    findLabel(value, list) {
      for (let i = 0; i < list.length; i++) {
        if (list[i][this.codeColumn] === value) {
          this.showValue = list[i][this.labelColumn];
          return true;
        }
        const children = list[i][this.childrenColumn];
        if (children && children.length > 0 && this.findLabel(value, children)) {
          return true;
        }
      }
      return false;
    },
    hasChildren(item) {
      const children = item[this.childrenColumn];
      return !!(children && children.length > 0);
    },
    // 打开选择框
    toShowDrawer() {
      this.showDrawer = true;
      if (this.type === "tree" && this.modelValue !== undefined && this.modelValue !== "") {
        // 有值：定位到该值所在的层，并回填面包屑
        this.selectedList = this.findTreeDataByValue(this.modelValue, this.dataList);
      } else {
        this.selectedList = [];
        this.currentList = this.dataList;
      }
    },
    // 选中一个值
    selectData(item) {
      const value = item[this.codeColumn];
      this.$emit("update:modelValue", value);
      this.showDrawer = false;
      if (value !== this.modelValue) {
        this.$emit("change", value);
      }
    },
    // 树节点：有下级就进下一级，没有下级就选中
    selectTreeData(item) {
      if (this.hasChildren(item)) {
        this.currentList = item[this.childrenColumn];
        this.selectedList.push(item);
        return;
      }
      this.selectData(item);
    },
    // 点面包屑最前面的根节点：回到最高一层，可重新选第一层的节点
    toRootLevel() {
      this.selectedList = [];
      this.currentList = this.dataList;
    },
    // 点面包屑回到上一层
    toParentLevel(item) {
      for (let i = 0; i < this.selectedList.length; i++) {
        if (this.selectedList[i] === item) {
          this.selectedList.splice(i + 1);
          break;
        }
      }
      this.currentList = item[this.childrenColumn] || [];
    },
    // 清除该查询条件
    toClear() {
      const hadValue = this.modelValue !== undefined && this.modelValue !== "";
      this.$emit("update:modelValue", "");
      this.showDrawer = false;
      if (hadValue) {
        this.$emit("change", "");
      }
    },
    // 树点「确认」：选中当前进入的最深一层
    toConfirm() {
      if (this.type === "tree" && this.selectedList.length > 0) {
        this.selectData(this.selectedList[this.selectedList.length - 1]);
      }
      this.showDrawer = false;
    },
    // 按值找到树上的整条路径，并把 currentList 定位到该值所在的那一层
    findTreeDataByValue(value, list) {
      for (let i = 0; i < list.length; i++) {
        if (list[i][this.codeColumn] === value) {
          this.currentList = list;
          return [list[i]];
        }
        const children = list[i][this.childrenColumn];
        if (children && children.length > 0) {
          const path = this.findTreeDataByValue(value, children);
          if (path.length > 0) {
            return [list[i]].concat(path);
          }
        }
      }
      return [];
    }
  }
};
</script>

<style lang="scss" scoped>
.mobile-condition-select{
  // 触发区：纯文字、无背景色
  // 统一原则：没有数据 = 浅色 + 提示词；有数据 = 正常黑
  .condition-area{
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    font-size: 12px;
    color: var(--yq-font-color-base);
    .text{
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      flex: none;
      margin-left: 3px;
      font-size: 10px;
      color: var(--yq-font-color-info);
    }
  }
}
.condition-data{
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 10px;
  .data-item{
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--el-border-color);
    font-size: var(--yq-font-size-large-mobile);
    line-height: 50px;
    color: var(--yq-font-color-tips);
    .column-label{
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .selected{
      flex: none;
      color: var(--el-color-success);
    }
  }
  .data-list{
    flex: 1;
    overflow: auto;
  }
  .tree{
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .header-selected{
      flex: none;
      min-height: 50px;
      line-height: 50px;
      .item{
        &.placeholder{
          color: var(--el-text-color-placeholder);
        }
        .label{
          font-size: var(--yq-font-size-large-mobile);
          color: var(--el-color-primary);
          padding: 5px 10px;
          background: #fafafa;
          border-radius: 4px;
        }
        .arrow{
          font-size: var(--yq-font-size-small-mobile);
          color: var(--yq-font-color-tips);
        }
      }
    }
    .tree-list{
      flex: 1;
      overflow: auto;
    }
  }
}
</style>
